import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import { Heading } from 'react-aria-components';

import Icon, { ICON_TYPES } from '@commons/Icon';
import { Dialog } from '@commons/Select/react-aria-components/dialog';
import { Modal } from '@commons/Select/react-aria-components/modal';

import { getTranslation } from '@services/translations';

import styles from './mobile-modal.scss';

const DEFAULT_CLOSE_TEXT = 'Close';

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

interface MobileModalProps {
  isOpen: boolean;
  isRTL?: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

// ----------------------------------------------------------------------
// UI
// ----------------------------------------------------------------------

export default function MobileModal({
  isOpen,
  isRTL = false,
  onClose,
  children,
  title = ''
}: MobileModalProps) {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const closeButtonLabel = getTranslation('TA_CLOSE', DEFAULT_CLOSE_TEXT);

  useEffect(() => {
    if (isOpen && modalBodyRef.current) {
      const input = modalBodyRef.current.querySelector<HTMLInputElement>('input');

      if (input) {
        requestAnimationFrame(() => {
          input.focus();
        });
      }
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      className={classNames(styles.modal, { [styles.rtl]: isRTL })}
    >
      <Dialog aria-label={title}>
        {/* Header */}
        <div className={styles.header}>
          <Heading slot="title" className={styles.label}>
            {title}
          </Heading>
          <button
            aria-label={closeButtonLabel}
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <Icon type={ICON_TYPES.CLOSE} />
          </button>
        </div>

        {/* Content */}
        <div ref={modalBodyRef} className={styles.body}>
          {children}
        </div>
      </Dialog>
    </Modal>
  );
}
